import { NoteSuggestionRefineActions } from "@/components/note/floating-actions/note-suggestion-refine-actions";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { NoteObservable } from "@/store/note/NoteObservable";
import {
  MDSButton,
  MDSIcon,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconKind,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";

interface NoteSuggestionActionsProps {
  editorStore: MemCommonEditorStore;
}

export const NoteSuggestionActions = observer(function NoteSuggestionActions({
  editorStore,
}: NoteSuggestionActionsProps) {
  const isRefineEnabled =
    editorStore.noteObservable instanceof NoteObservable &&
    !!editorStore.noteObservable.suggestionData;
  const [showRefineInput, setShowRefineInput] = useState(false);
  const [pendingAction, setPendingAction] = useState<"accept" | "reject" | null>(null);
  const isSubmitting = pendingAction !== null;
  const isMountedRef = useRef(true);
  const isSubmittingRef = useRef(false);
  const acceptTooltipLabel = (() => {
    const suggestionKind =
      editorStore.noteObservable instanceof NoteObservable
        ? editorStore.noteObservable.suggestionData?.kind
        : undefined;

    switch (suggestionKind) {
      case "VOICE_SESSION_V1":
      case "VOICE_SESSION_REFINE_V1":
        return "Accept Voice changes";
      case "NOTE_CLEANUP_V1":
      case "NOTE_CLEANUP_REFINE_V1":
        return "Accept Clean up changes";
      default:
        return "Accept changes";
    }
  })();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleCloseSuggestion = async (accept: boolean) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setPendingAction(accept ? "accept" : "reject");
    try {
      await editorStore.closeSuggestion(accept);
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) {
        setPendingAction(null);
      }
    }
  };

  if (showRefineInput && editorStore.noteObservable instanceof NoteObservable) {
    return (
      <NoteSuggestionRefineActions
        noteObservable={editorStore.noteObservable}
        setShowRefineInput={setShowRefineInput}
      />
    );
  }

  return (
    <StyledContainer>
      <MDSText variant={MDSTextVariant.TextSm}>Edited Note</MDSText>
      <StyledSButton
        isDisabled={!isRefineEnabled || isSubmitting}
        onClick={() => setShowRefineInput(true)}
      >
        <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Sparkle} />
        Refine
      </StyledSButton>
      <MDSTooltip
        config={getShortcutTooltipConfig({
          action: "accept_voice_cleanup_changes",
          label: acceptTooltipLabel,
          placement: MDSTooltipPlacement.Top,
        })}
      >
        <StyledSButton
          variant={MDSButtonVariant.Contrast}
          isDisabled={isSubmitting}
          isLoading={pendingAction === "accept"}
          onClick={() => handleCloseSuggestion(true)}
        >
          <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Check} />
          Accept
        </StyledSButton>
      </MDSTooltip>
      <MDSIconButton
        onClick={() => handleCloseSuggestion(false)}
        iconKind={MDSIconKind.Exit}
        size={MDSIconButtonSize.Medium}
        isDisabled={isSubmitting}
        isLoading={pendingAction === "reject"}
      />
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  gap: ${({ theme }) => theme.spacing["2"]};
  justify-content: flex-start;
  padding: ${({ theme }) =>
    `${theme.spacing["4"]} ${theme.spacing["4"]} ${theme.spacing["4"]} ${theme.spacing["6"]}`};
`;

const StyledSButton = styled(MDSButton)`
  width: 122.333px;
`;
